import React, { useState, useEffect, useContext } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header";
import SettingsTabsNavigation from "../../../components/Tabs/SettingsTabsNavigation";
import FieldConfigurationSidebar from "../../../components/Tabs/FieldConfigurationSidebar";
import { showSuccessAlert, showConfirmationAlert, showToastSuccess, showUnsavedChangesAlert } from "../../uiInterface/base-ui/alertservice";
import { LifecycleStatusReasonService, LifecycleStatusReason } from "./lifecycleStatusReason.service";
import Tooltip from "@mui/material/Tooltip";
import { AuthContext } from "../../../context/authcontext";
import { useTenantRole } from "../../../components/TenantRoleDisplay";

const route = all_routes;

const WonReasons = () => {
  const [statuses, setStatuses] = useState<LifecycleStatusReason[]>([]);
  const [originalStatuses, setOriginalStatuses] = useState<LifecycleStatusReason[]>([]);
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedTenantId } = useContext(AuthContext);
  const tenantId = Number(selectedTenantId);
  const userRole = useTenantRole(selectedTenantId || 0);
  const isSalesUser = userRole?.role_key === "SALESUSER";

  useEffect(() => {
    if (!selectedTenantId) return;
    fetchData();
  }, [selectedTenantId]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const categories = await LifecycleStatusReasonService.getCategories();
      const wonCategory = categories.find((c) => c.lifecycle_status_reason_category_key === "WON");

      if (wonCategory) {
        setCategoryId(wonCategory.lifecycle_status_reason_category_id);
        const reasons = await LifecycleStatusReasonService.getReasons(tenantId);
        const filtered = reasons.filter((r) => r.lifecycle_status_reason_category_id === wonCategory.lifecycle_status_reason_category_id);
        setStatuses(filtered);
        setOriginalStatuses(JSON.parse(JSON.stringify(filtered)));
      }
    } catch (error) {
      console.error("Failed to fetch Won Reasons:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const save = async () => {
    try {
      for (const status of statuses) {
        if (!status.reason_name.trim()) continue;

        if (status.lifecycle_status_reason_id) {
          const original = originalStatuses.find(o => o.lifecycle_status_reason_id === status.lifecycle_status_reason_id);
          const hasChanged = !original || original.reason_name !== status.reason_name || original.is_active !== status.is_active;

          if (hasChanged) {
            await LifecycleStatusReasonService.updateReason(status.lifecycle_status_reason_id, {
              reason_name: status.reason_name,
              is_active: status.is_active,
            }, tenantId);
          }
        } else {
          await LifecycleStatusReasonService.createReason({
            reason_name: status.reason_name,
            is_active: status.is_active,
            tenant_id: tenantId,
            lifecycle_status_reason_category_id: categoryId as number,
          });
        }
      }
      await showSuccessAlert("Won Reasons updated successfully.");
      await fetchData();
    } catch (error) {
      console.error("Failed to save Won Reasons:", error);
    }
  };

  useEffect(() => {
    const hasUnsaved = JSON.stringify(statuses) !== JSON.stringify(originalStatuses);

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsaved) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    const handleLinkClick = (e: MouseEvent) => {
      if (!hasUnsaved) return;
      const target = e.target as HTMLElement;
      const anchor = target.closest("a");

      if (anchor && anchor.href && !anchor.hasAttribute("target") && !anchor.hasAttribute("download")) {
        const url = new URL(anchor.href);
        if (url.origin === window.location.origin && url.pathname !== window.location.pathname) {
          e.preventDefault();
          e.stopPropagation();

          showUnsavedChangesAlert().then(async (result) => {
            if (result.isConfirmed) {
              await save();
              navigate(url.pathname + url.search + url.hash);
            } else if (result.isDenied) {
              setStatuses(JSON.parse(JSON.stringify(originalStatuses)));
              navigate(url.pathname + url.search + url.hash);
            }
          });
        }
      }
    };

    document.addEventListener("click", handleLinkClick, true);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("click", handleLinkClick, true);
    };
  }, [statuses, originalStatuses]);

  const updateStageName = (idx: number, value: string) => {
    const updated = [...statuses];
    updated[idx].reason_name = value;
    setStatuses(updated);
  };

  const toggleStatusActive = (idx: number) => {
    const updated = [...statuses];
    updated[idx].is_active = !updated[idx].is_active;
    setStatuses(updated);
  };

  const addStage = () => {
    if (!categoryId) return;
    const newStage: LifecycleStatusReason = {
      reason_name: "",
      is_active: true,
      tenant_id: tenantId,
      lifecycle_status_reason_category_id: categoryId,
    };
    setStatuses([...statuses, newStage]);
  };

  const removeStage = async (idx: number) => {
    const stage = statuses[idx];

    const result = await showConfirmationAlert(
      "Are you sure?",
      "You won't be able to revert this status reason!"
    );

    if (result.isConfirmed) {
      if (stage.lifecycle_status_reason_id) {
        try {
          await LifecycleStatusReasonService.deleteReason(stage.lifecycle_status_reason_id);
          showToastSuccess("Status reason deleted successfully");
          setOriginalStatuses(prev => prev.filter(s => s.lifecycle_status_reason_id !== stage.lifecycle_status_reason_id));
        } catch (error) {
          console.error("Failed to delete reason:", error);
          return;
        }
      }
      const updated = [...statuses];
      updated.splice(idx, 1);
      setStatuses(updated);
    }
  };

  const cancel = () => {
    fetchData();
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title">Settings</h4>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons">
                    <CollapseHeader />
                  </div>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-body pb-0 pt-2">
                <SettingsTabsNavigation
                  activeTab="field-configuration"
                  variant="bottom"
                  includeIcons={true}
                  context="organization"
                />
              </div>
            </div>

            <div className="row">
              <FieldConfigurationSidebar />

              <div className="col-xl-9 col-lg-12">
                <div className="card">
                  <div className="card-body">
                    <div className="border-bottom mb-3 pb-3">
                      <h4 className="fw-semibold mb-1">Won Reasons</h4>
                      <p>Configure the won reasons below</p>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-medium">
                        Reason Options
                      </label>
                    </div>

                    {isLoading ? (
                      <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    ) : (
                      <>
                        {statuses.map((stage, idx) => (
                          <div
                            key={stage.lifecycle_status_reason_id || `temp-${idx}`}
                            className="d-flex align-items-center mb-2 p-2 border rounded w-20"
                            style={{
                              opacity: stage.is_active ? 1 : 0.6,
                              backgroundColor: stage.is_active ? "inherit" : "#f8f9fa"
                            }}
                          >
                            <input
                              type="text"
                              className="form-control me-3"
                              placeholder="Reason name"
                              value={stage.reason_name}
                              onChange={(e) =>
                                updateStageName(idx, e.target.value)
                              }
                              disabled={isSalesUser}
                            />
                            <Tooltip title={stage.is_active ? "Deactivate" : "Activate"} placement="top" arrow>
                              <div className="form-check form-switch me-1">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  role="switch"
                                  checked={stage.is_active}
                                  onChange={() => toggleStatusActive(idx)}
                                  style={{ cursor: "pointer" }}
                                  disabled={isSalesUser}
                                />
                              </div>
                            </Tooltip>
                            {!isSalesUser && (
                              <Tooltip title="Delete" placement="top" arrow>
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={(e) => {
                                    removeStage(idx);
                                    e.currentTarget.blur();
                                  }}
                                >
                                  <i className="fa fa-trash" />
                                </button>
                              </Tooltip>
                            )}
                          </div>
                        ))}

                        {!isSalesUser && (
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm mt-2 mb-4"
                            onClick={(e) => {
                              addStage();
                              e.currentTarget.blur();
                            }}
                          >
                            <i className="fa fa-plus" /> Add Reason
                          </button>
                        )}
                      </>
                    )}

                    {!isSalesUser && (
                      <div>
                        <button
                          type="button"
                          className="btn btn-outline-primary me-2"
                          onClick={(e) => {
                            cancel();
                            e.currentTarget.blur();
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary me-2"
                          onClick={(e) => {
                            save();
                            e.currentTarget.blur();
                          }}
                        >
                          Save Changes
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WonReasons;

